import { PIN_HOVER_CSS } from '../../../utils/pinHoverCss.js';

// Shared Leaflet CSS styles for both tracking and weather maps

export const getLeafletStyles = (isDark, styleId) => `
  .leaflet-popup-content-wrapper {
    background-color: ${isDark ? '#1d1d1d' : '#ffffff'};
    color: ${isDark ? '#f0f5f2' : '#1f2937'};
    --harvest-not-producing-text: ${isDark ? '#9CA3AF' : '#6B7280'};
    border-radius: 8px;
    border: 1px solid ${isDark ? '#333' : '#e5e7eb'};
  }
  .leaflet-popup-tip {
    background-color: ${isDark ? '#1d1d1d' : '#ffffff'};
    border: 1px solid ${isDark ? '#333' : '#e5e7eb'};
  }
  .leaflet-popup-close-button {
    color: ${isDark ? '#888' : '#6b7280'} !important;
  }
  .leaflet-popup-close-button:hover {
    color: var(--bloom-green) !important;
  }
  .leaflet-container {
    background-color: ${isDark ? '#1d1d1d' : '#f3f4f6'};
  }
  .leaflet-control-attribution {
    background-color: ${isDark ? 'rgba(29, 29, 29, 0.8)' : 'rgba(255, 255, 255, 0.8)'} !important;
    color: ${isDark ? '#666' : '#6b7280'} !important;
    font-size: 10px !important;
  }
  .leaflet-control-attribution a {
    color: var(--bloom-green) !important;
  }
  .leaflet-control-zoom a {
    background-color: ${isDark ? '#282a29' : '#ffffff'} !important;
    color: ${isDark ? '#f0f5f2' : '#1f2937'} !important;
    border-color: ${isDark ? '#333' : '#d1d5db'} !important;
  }
  .leaflet-control-zoom a:hover {
    background-color: var(--bloom-green) !important;
    color: ${isDark ? '#1d1d1d' : '#ffffff'} !important;
  }
  .ship-marker {
    background: transparent !important;
    border: none !important;
  }
  .port-label {
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
    font-size: 10px;
    font-weight: 600;
    color: ${isDark ? '#d1d5db' : '#4b5563'};
  }
  .port-label::before {
    display: none;
  }
  .port-tooltip {
    font-size: 11px;
    font-weight: 600;
  }
  .weather-map-container .leaflet-interactive {
    cursor: pointer !important;
    -webkit-tap-highlight-color: rgba(21, 203, 94, 0.3) !important;
    touch-action: manipulation !important;
  }
  .weather-map-container .leaflet-tile-pane {
    pointer-events: none !important;
  }
  .leaflet-pin-icon {
    background: transparent !important;
    border: none !important;
  }
  ${PIN_HOVER_CSS}
  .hazard-icon svg {
    display: block;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.35));
    opacity: 0.8; /* let the precipitation layer show through */
    transition: opacity 0.15s;
  }
  .hazard-icon:focus:not(:focus-visible) {
    outline: none; /* no focus square after a mouse click; keyboard focus keeps its ring */
  }
  .hazard-icon:hover svg {
    opacity: 1;
  }
  .leaflet-pin-icon--muted {
    opacity: 0.9;
  }
  .storm-icon {
    background: transparent !important;
    border: none !important;
  }
  .storm-icon svg {
    display: block;
    filter: drop-shadow(0 1px 1.5px rgba(15, 23, 42, 0.35));
  }
  /* The glyph is scaled by a centred inner wrapper (24..48 px equivalent); Leaflet's outer transform is untouched. */
  .storm-icon__inner {
    transform-origin: 50% 50%;
    will-change: transform;
  }
  .storm-icon--glide {
    transition: transform var(--storm-glide, 1500ms) linear;
  }
  @media (prefers-reduced-motion: reduce) {
    .storm-icon--glide,
    .storm-icon__inner {
      transition: none !important;
    }
  }
  /* Hazard panes never take pointer events; the track, wind zones and chips carry no blur or shadow. */
  .leaflet-hazard-fill-pane,
  .leaflet-hazard-line-pane,
  .leaflet-hazard-label-pane,
  .leaflet-hazard-fill-pane *,
  .leaflet-hazard-line-pane *,
  .leaflet-hazard-label-pane * {
    pointer-events: none !important;
  }
  .hazard-fade {
    transition: stroke-opacity 150ms ease-out, fill-opacity 150ms ease-out;
  }
  @media (prefers-reduced-motion: reduce) {
    .hazard-fade { transition: none; }
  }
  .hazard-pin-icon,
  .hazard-chip-icon {
    background: transparent !important;
    border: none !important;
  }
  .hazard-disc {
    position: absolute;
    left: 0;
    top: 0;
    box-sizing: border-box;
    width: 20.5px;
    height: 20.5px;
    margin: -10.25px 0 0 -10.25px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2.5px solid #ffffff;
    border-radius: 50%;
    color: #ffffff;
    font: 700 9px/1 Arial, Helvetica, sans-serif;
    letter-spacing: 0;
  }
  .hazard-disc--latest {
    width: 24.5px;
    height: 24.5px;
    margin: -12.25px 0 0 -12.25px;
  }
  .hazard-glyph {
    position: absolute;
    left: 0;
    top: 0;
    display: block;
    margin: -7px 0 0 -7px;
  }
  .hazard-glyph svg {
    display: block;
  }
  .hazard-vertex-label {
    position: absolute;
    left: 0;
    top: 0;
    box-sizing: border-box;
    white-space: nowrap;
    background: ${isDark ? 'rgba(20, 35, 47, 0.96)' : 'rgba(255, 255, 255, 0.96)'};
    color: ${isDark ? '#f8fafc' : '#172b36'};
    border: 1px solid ${isDark ? '#547083' : '#cbd5e1'};
    box-shadow: none;
    font: 700 12px/1.1 Arial, Helvetica, sans-serif;
    padding: 3px 6px;
    border-radius: 4px;
  }
  .hazard-vertex-label--active {
    border-color: #16b84e;
  }
  .weather-map-container .leaflet-top,
  .weather-map-container .leaflet-bottom {
    z-index: 1000 !important;
  }

  .produce-map-controls .leaflet-control-zoom {
    display: flex !important;
    flex-direction: column;
    gap: 2px;
    border: none !important;
    box-shadow: none !important;
    margin: 12px 0 0 12px !important;
  }
  .produce-map-controls .leaflet-control-zoom a {
    box-sizing: border-box !important;
    display: flex !important;
    align-items: center;
    justify-content: center;
    width: 28px !important;
    height: 28px !important;
    line-height: 1 !important;
    border: 1px solid ${isDark ? '#30363d' : '#E2E6E2'} !important;
    border-radius: 6px !important;
    background-color: ${isDark ? '#282a29' : '#ffffff'} !important;
    color: ${isDark ? '#f0f5f2' : '#5D625F'} !important;
    font-size: 16px !important;
    font-weight: 400 !important;
    box-shadow: none !important;
    transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease;
  }
  .produce-map-controls .leaflet-control-zoom a:hover {
    background-color: ${isDark ? '#333534' : '#F0F4F1'} !important;
    color: ${isDark ? '#ffffff' : '#282A29'} !important;
  }
  .produce-map-controls .leaflet-control-zoom a.leaflet-disabled {
    background-color: ${isDark ? '#282a29' : '#ffffff'} !important;
    border-color: ${isDark ? '#30363d' : '#E2E6E2'} !important;
    color: ${isDark ? '#5d625f' : '#C3C4C3'} !important;
  }
`;

export const injectLeafletStyles = (isDark, styleId) => {
  let styleEl = document.getElementById(styleId);
  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = styleId;
    document.head.appendChild(styleEl);
  }
  styleEl.textContent = getLeafletStyles(isDark, styleId);
  return () => { styleEl.remove(); };
};
